/*
 * OnboardingComplete — new final step shown after the wizard succeeds.
 *
 * On mount, POSTs /api/onboarding/claim-welcome-rewards which credits the
 * ledger idempotently and returns the manifest. Feeds the manifest into
 * <CoinReveal>. Claim button navigates to the eroscoach session.
 *
 * Purely idempotent — refreshing the tab will return `already_claimed: true`
 * and replay the ceremony without double-crediting.
 */
import React, { useEffect, useState } from "react";
import { authAxios } from "../../utils/auth";
import AmbientStarfield from "./shared/AmbientStarfield";
import CoinReveal from "./CoinReveal";
import "./OnboardingBookends.css";

export const OnboardingComplete = ({
  userName = "Friend",
  partnerName = "them",
  onClaim,
  claimEndpoint = "/onboarding/claim-welcome-rewards",
  soundEnabled = true,
}) => {
  const [manifest, setManifest] = useState(null);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authAxios.post(claimEndpoint);
        if (cancelled) return;
        setManifest(res.data);
      } catch (e) {
        console.error("claim-welcome-rewards failed:", e);
        if (cancelled) return;
        setLoadError(
          e?.response?.data?.detail || "Unable to load rewards. Please try again."
        );
      }
    })();
    return () => { cancelled = true; };
  }, [claimEndpoint]);

  if (loadError) {
    return (
      <div className="ob-scene" data-testid="onboarding-complete-error">
        <AmbientStarfield />
        <div className="ob-panel">
          <div className="ob-eyebrow ob-rise">Something went wrong</div>
          <p className="ob-body ob-rise ob-rise-2">{loadError}</p>
          <button
            className="ob-cta ob-rise ob-rise-4"
            onClick={onClaim}
            data-testid="onboarding-complete-skip"
          >
            CONTINUE
          </button>
        </div>
      </div>
    );
  }

  if (!manifest) {
    return (
      <div className="ob-scene" data-testid="onboarding-complete-loading">
        <AmbientStarfield />
        <div className="ob-panel">
          <div className="ob-eyebrow ob-rise">Preparing your rewards…</div>
        </div>
      </div>
    );
  }

  return (
    <div className="ob-scene" data-testid="onboarding-complete">
      <AmbientStarfield />
      <div className="ob-panel">
        <div className="ob-eyebrow ob-rise">Profile Basics Complete</div>
        <h1 className="ob-title ob-rise ob-rise-1">Thank you, {userName}</h1>
        <p className="ob-body ob-rise ob-rise-2">
          Agent Eros is now ready to guide you &amp; {partnerName}.
          Your basic foundation is set &amp; here is your welcome reward.
        </p>
        <CoinReveal
          claims={manifest.claims || []}
          totalLabel="TOTAL"
          claimLabel="START YOUR 1st SESSION"
          claimSubtext="Your first session begins instantly"
          onClaim={onClaim}
          replayable
          soundEnabled={soundEnabled}
          autoStart
          showBreakdown
        />
      </div>
    </div>
  );
};

export default OnboardingComplete;
